/**
 * Opens what a notification — or one of its actions — links to (I-2915).
 *
 * A link is app-neutral, `<module key>/<resource type>/<resource id>[?query]`
 * ("mail/email/<uuid>?mailbox=<uuid>"), so every client opens it its own way.
 * Here the app whose key is the first segment says where the resource lives:
 * its `@<app>/connect` module declares a route per resource type
 * (`ConnectAppModule.resourceLinks`). An unknown app, an app the account may
 * not use, a resource type the app does not declare: nothing opens.
 *
 * A link with a leading `/` is a web path of an older producer (ERP, the
 * mentions of before) and goes through the ERP's opener as it always did.
 */
import {
  isNavigationFailure,
  NavigationFailureType,
  type Router,
} from "vue-router";
import {
  parseConnectResourceLink,
  resolveConnectResourceLink,
} from "@blpde/connect";
import { loadDeepLinks } from "@erp/connect/shell";
import { useNxToasts } from "@blpde/nx-ui";
import { isAppEntitled } from "@/applications";
import i18n from "@/i18n";
import { showSupportRequest } from "@/support/useSupportRequests";
import { apps } from "@/integration";
import { installConnectApp } from "@/integration/loader";

export interface OpenNotificationLinkOptions {
  /** The master company of the notification; the owning app opens the resource in it. */
  companyID?: number;
}

interface ResolvedLink {
  /** The route path with its query. */
  to: string;
  /** The base path of the app that owns it. */
  basePath: string;
}

function below(path: string, basePath: string): boolean {
  return path === basePath || path.startsWith(`${basePath}/`);
}

async function resolve(
  router: Router,
  link: string,
  options: OpenNotificationLinkOptions,
): Promise<ResolvedLink | null> {
  const parsed = parseConnectResourceLink(link);
  if (!parsed) return null;
  const app = apps.find((entry) => entry.id === parsed.module);
  if (!app || !isAppEntitled(app.id)) return null;
  let links;
  try {
    links = (await installConnectApp(router, app.id)).resourceLinks;
  } catch {
    return null;
  }
  const to = resolveConnectResourceLink(links, {
    ...parsed,
    ...(options.companyID ? { companyID: options.companyID } : {}),
  });
  // An app only answers for its own routes.
  if (!to || !below(to.split(/[?#]/, 1)[0], app.basePath)) return null;
  return { to, basePath: app.basePath };
}

/**
 * The route an app-neutral link leads to; null when nothing can open it.
 * Loads the owning app's module (and mounts its routes) to ask it.
 */
export async function resolveNotificationLink(
  router: Router,
  link: string,
  options: OpenNotificationLinkOptions = {},
): Promise<string | null> {
  return (await resolve(router, link, options))?.to ?? null;
}

/**
 * Opens a notification's link. True when the navigation reached its target —
 * only then the caller marks the notification read, so a link that leads
 * nowhere keeps it unread.
 */
export async function openNotificationLink(
  router: Router,
  link: string | undefined,
  options: OpenNotificationLinkOptions = {},
): Promise<boolean> {
  const raw = (link ?? "").trim();
  if (!raw) return false;
  if (raw.startsWith("/")) {
    // Same host-aware resolver as MentionsView (dead /bdms/… paths → host + focus).
    const { openMentionDeepLink } = await loadDeepLinks();
    return openMentionDeepLink(router, raw);
  }
  // The remote support request notification (`admin/support_request/<id>`) is
  // answered by the shell's own approval dialog — whether or not the account
  // may use the admin app, which only owns the link's name.
  const parsed = parseConnectResourceLink(raw);
  if (parsed?.module === "admin" && parsed.resourceType === "support_request") {
    if (await showSupportRequest(parsed.resourceID)) return true;
    useNxToasts().showToast(i18n.global.t("connect.support.errors.notOpen"), "info");
    return false;
  }
  const target = await resolve(router, raw, options);
  if (!target) return false;
  let failure: unknown;
  try {
    failure = await router.push(target.to);
  } catch {
    return false;
  }
  // vue-router does not throw for a navigation that did not happen: it
  // resolves with the failure. Stopped by a guard (unsaved work) or overtaken
  // by another navigation, the target did not open — wherever the route
  // stands now.
  if (
    isNavigationFailure(
      failure,
      NavigationFailureType.aborted | NavigationFailureType.cancelled,
    )
  ) {
    return false;
  }
  if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
    // Nothing moved: opened only when this exact target is what is shown.
    return (
      router.currentRoute.value.fullPath === router.resolve(target.to).fullPath
    );
  }
  // A guard may have led elsewhere (no access): only the owning app counts
  // as reached. The app's view takes it from there.
  return below(router.currentRoute.value.path, target.basePath);
}
